"use client";

import { createContext, useContext, useState, useEffect } from "react";
import {
  type Currency,
  DEFAULT_CURRENCY,
} from "@/app/utils/currency-config";

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
});

function getCurrencyFromCookie(): Currency | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith("preferred-currency="));
  if (!match) return null;
  const value = match.split("=")[1];
  if (value === "CHF" || value === "EUR" || value === "USD") return value;
  return null;
}

interface CurrencyProviderProps {
  children: React.ReactNode;
  /**
   * Currency resolved on the server (middleware x-currency header). Seeds the
   * first render so server HTML and hydration show the same prices.
   */
  initialCurrency?: Currency;
}

export function CurrencyProvider({
  children,
  initialCurrency = DEFAULT_CURRENCY,
}: CurrencyProviderProps) {
  const [currency, setCurrencyState] = useState<Currency>(initialCurrency);

  useEffect(() => {
    const fromCookie = getCurrencyFromCookie();
    if (fromCookie) {
      setCurrencyState(fromCookie);
    }
  }, []);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    document.cookie = `preferred-currency=${newCurrency};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  return useContext(CurrencyContext);
}
